import React, { useState, useCallback, useEffect, memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./AuthPopup.css";
import {
  validateLoginForm,
  validateSignupForm,
} from "../../utils/validation";
import SocialAuthButtons from "./SocialAuthButtons";
import PhoneVerificationField, {
  PhoneVerificationValue,
} from "./PhoneVerificationField";

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (emailOrNickname: string, password: string) => Promise<void>;
  onSignup: (
    nickname: string,
    email: string,
    password: string,
    subscribed: boolean,
    phone: string,
    phoneVerificationToken: string,
    fullname: string
  ) => Promise<void>;
  mode?: "login" | "link";
  onVk?: () => void;
  onBridge?: (provider: "vk") => void;
}

const AuthPopup: React.FC<AuthPopupProps> = memo(
  ({ isOpen, onClose, onLogin, onSignup, mode = "login", onVk, onBridge }) => {
    const { t } = useTranslation();
    const isLink = mode === "link";
    const [activeTab, setActiveTab] = useState<"login" | "signup">("login");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const errorRef = useRef<HTMLDivElement | null>(null);

    // Login form state (supports email or nickname)
    const [loginData, setLoginData] = useState({
      emailOrNickname: "",
      password: "",
    });

    // Signup form state
    const [signupData, setSignupData] = useState({
      nickname: "",
      email: "",
      password: "",
      confirmPassword: "",
      fullname: "",
    });
    const [phoneVerification, setPhoneVerification] = useState<PhoneVerificationValue>({
      phone: "+7",
      verificationToken: null,
    });

    useEffect(() => {
      if (!isOpen) {
        setPhoneVerification({ phone: "+7", verificationToken: null });
      }
    }, [isOpen]);

    const isPhoneVerified = !!phoneVerification.verificationToken;

    useEffect(() => {
      if (!error) return;
      errorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }, [error]);

    const formError = error ? (
      <div
        ref={errorRef}
        className="auth-error auth-error--form"
        role="alert"
        aria-live="assertive"
        tabIndex={-1}
      >
        {error}
      </div>
    ) : null;

    const handleLogin = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const validation = validateLoginForm(loginData);
        if (!validation.valid) {
          setError(validation.error || t("validation.invalidInput"));
          return;
        }

        setLoading(true);
        setError(null);

        try {
          await onLogin(loginData.emailOrNickname, loginData.password);
          onClose();
        } catch (err: any) {
          console.error("Login error in AuthPopup:", err);
          setError(err.message || t("auth.loginFailed"));
        } finally {
          setLoading(false);
        }
      },
      [loginData, onLogin, onClose, t]
    );

    const handleSignup = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form with confirm password check
        const validation = validateSignupForm(
          { ...signupData, phone: phoneVerification.phone },
          signupData.confirmPassword,
        );
        if (!validation.valid) {
          setError(validation.error || t("validation.invalidInput"));
          return;
        }

        if (!isPhoneVerified) {
          setError(t("auth.phoneMustBeVerified"));
          return;
        }

        setLoading(true);
        setError(null);

        try {
          await onSignup(
            signupData.nickname,
            signupData.email,
            signupData.password,
            false,
            phoneVerification.phone,
            phoneVerification.verificationToken || "",
            signupData.fullname
          );
          onClose();
        } catch (err: any) {
          setError(err.message || t("auth.signupFailed"));
        } finally {
          setLoading(false);
        }
      },
      [
        signupData,
        phoneVerification,
        isPhoneVerified,
        onSignup,
        onClose,
        t,
      ]
    );

    const clearError = () => setError(null);

    if (!isOpen) return null;

    return (
      <div className="auth-popup-overlay" onClick={onClose}>
        <div className="auth-popup" onClick={(e) => e.stopPropagation()}>
          <div className="auth-popup-topbar">
            <button
              className="auth-popup-close"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              ×
            </button>
          </div>

          {!isLink && (
            <div className="auth-popup-tabs">
              <button
                className={`auth-tab ${activeTab === "login" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("login");
                  setPhoneVerification({ phone: "+7", verificationToken: null });
                  setError(null);
                }}
              >
                {t("auth.login")}
              </button>
              <button
                className={`auth-tab ${activeTab === "signup" ? "active" : ""}`}
                onClick={() => setActiveTab("signup")}
              >
                {t("auth.signup")}
              </button>
            </div>
          )}

          {isLink || activeTab === "login" ? (
            <form onSubmit={handleLogin} className="auth-form">
              <input
                type="text"
                placeholder={t("auth.emailOrNickname")}
                value={loginData.emailOrNickname}
                onChange={(e) => {
                  setLoginData({ ...loginData, emailOrNickname: e.target.value });
                  clearError();
                }}
                disabled={loading}
                autoCapitalize="none"
                autoComplete="username"
                required
              />
              <input
                type="password"
                placeholder={t("auth.password")}
                value={loginData.password}
                onChange={(e) => {
                  setLoginData({ ...loginData, password: e.target.value });
                  clearError();
                }}
                disabled={loading}
                autoComplete="current-password"
                required
              />
              <button type="submit" disabled={loading} className="auth-submit">
                {loading
                  ? isLink
                    ? t("auth.linking")
                    : t("auth.loggingIn")
                  : isLink
                    ? t("auth.linkAccount")
                    : t("auth.login")}
              </button>
              {formError}
            </form>
          ) : (
            <form onSubmit={handleSignup} className="auth-form">
              <input
                type="text"
                placeholder={t("auth.nickname")}
                value={signupData.nickname}
                onChange={(e) => {
                  setSignupData({
                    ...signupData,
                    nickname: e.target.value.replace(/\s+/g, ""),
                  });
                  clearError();
                }}
                disabled={loading}
                maxLength={25}
                autoCapitalize="none"
                autoComplete="username"
                required
              />
              <input
                type="email"
                placeholder={t("auth.email")}
                value={signupData.email}
                onChange={(e) => {
                  setSignupData({ ...signupData, email: e.target.value });
                  clearError();
                }}
                disabled={loading}
                maxLength={64}
                autoComplete="email"
                required
              />
              <PhoneVerificationField
                disabled={loading}
                initialPhone={phoneVerification.phone}
                onChange={(value) => {
                  setPhoneVerification(value);
                  if (value.verificationToken) clearError();
                }}
                onError={setError}
                testIdPrefix="signup"
              />
              <input
                type="text"
                placeholder={t("auth.fullName")}
                value={signupData.fullname}
                onChange={(e) => {
                  setSignupData({ ...signupData, fullname: e.target.value });
                  clearError();
                }}
                disabled={loading}
                maxLength={128}
                autoComplete="name"
                required
              />
              <input
                type="password"
                placeholder={t("auth.passwordMin")}
                value={signupData.password}
                onChange={(e) => {
                  setSignupData({ ...signupData, password: e.target.value });
                  clearError();
                }}
                disabled={loading}
                maxLength={64}
                autoComplete="new-password"
                required
              />
              <input
                type="password"
                placeholder={t("auth.confirmPassword")}
                value={signupData.confirmPassword}
                onChange={(e) => {
                  setSignupData({ ...signupData, confirmPassword: e.target.value });
                  clearError();
                }}
                disabled={loading}
                maxLength={64}
                autoComplete="new-password"
                required
              />
              <button type="submit" disabled={loading} className="auth-submit">
                {loading ? t("auth.signingUp") : t("auth.signup")}
              </button>
              {formError}
            </form>
          )}
          {!isLink && activeTab === "login" && (
            <SocialAuthButtons onVk={onVk} onBridge={onBridge} />
          )}
        </div>
      </div>
    );
  }
);

AuthPopup.displayName = "AuthPopup";

export default AuthPopup;
